import { FILTER_VARIANTS } from "~/constants/filters/filterVariants";

export type EmptinessVariant =
  | typeof FILTER_VARIANTS.IS_EMPTY
  | typeof FILTER_VARIANTS.IS_NOT_EMPTY;

/** A custom-field filter asking whether the field is (not) empty. */
export const isEmptinessFilter = (
  value: unknown,
): value is { filterVariant: EmptinessVariant } => {
  const variant = (value as { filterVariant?: unknown } | null)?.filterVariant;
  return (
    variant === FILTER_VARIANTS.IS_EMPTY ||
    variant === FILTER_VARIANTS.IS_NOT_EMPTY
  );
};

export const emptinessFilter = (variant: EmptinessVariant) => ({
  filterVariant: variant,
});

const isFilled = (value: unknown): boolean =>
  value != null && String(value).trim() !== "";

/**
 * Whether a filter value narrows the list. Leftovers of a cleared input
 * (`""`, `[]`, `["", ""]`, `{ value: "" }`) do not; a `false` Yes/No choice and
 * an emptiness condition do.
 */
export const isFilterApplied = (value: any): boolean => {
  if (typeof value === "boolean") return true;
  if (isEmptinessFilter(value)) return true;
  if (Array.isArray(value)) return value.some(isFilled);
  if (value && typeof value === "object" && "value" in value) {
    return Array.isArray(value.value)
      ? value.value.some(isFilled)
      : isFilled(value.value);
  }
  return isFilled(value);
};

// Custom fields are keyed by their id; built-in filters by a name like "TEAMS"
const MONGO_ID = /^[a-f\d]{24}$/i;

/** Whether an active filter should be sent to the backend as a custom field. */
export const isCustomFieldFilter = (key: string, value: unknown): boolean =>
  MONGO_ID.test(key) && isFilterApplied(value);

/**
 * Keeps only the saved filters a list still knows. A filter that was renamed,
 * removed or belongs to another list would otherwise stay applied with no
 * chip to show or clear it.
 */
export const pickKnownActiveFilters = (
  filters: Record<string, unknown> | null | undefined,
  knownIds: Set<string>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(filters || {}).filter(([id]) => knownIds.has(id)),
  );

/** Ids of saved filters that narrow the list; each of them needs a chip. */
export const getAppliedFilterIds = (
  filters: Record<string, unknown> | null | undefined,
): string[] =>
  Object.keys(filters || {}).filter((id) => isFilterApplied(filters?.[id]));
